import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { yieldCallback } from '@zillow/yield-callback';
import { event } from '@zillow/universal-analytics';
import { Box, Text, BUTTON_TYPES, IconArrowRight, Button, token, IconButton } from '@zillow/constellation';
import UpsellCard from './UpsellCard.js';
import UpsellHeading from './UpsellHeading.js';
import { ColorPropType, SizePropType } from './prop-types.js';
import { MEDIA_DESKTOP, MEDIA_TABLET, BREAKPOINT_TABLET, BREAKPOINT_DESKTOP } from './constants.js';
import { usePageProps } from '../PagePropsProvider/PagePropsProvider.js';
import { getCmsInfoForPage } from '../../utils/clickstream/getCmsInfoForPage.js';

/* eslint-disable no-confusing-arrow */
var Upsell = function Upsell(_ref) {
  var _ref2, _ref3, _ref4, _ref5, _md, _lg, _ref6, _ref7;
  var body = _ref.body,
    button = _ref.button,
    className = _ref.className,
    color = _ref.color,
    editorMode = _ref.editorMode,
    heading = _ref.heading,
    id = _ref.id,
    image = _ref.image,
    legal = _ref.legal,
    size = _ref.size,
    url = _ref.url;
  var pageProps = usePageProps();
  var hasNonXsArrowCta = button === '' && ['sm', 'md', 'lg'].includes(size);
  var hasImage = size === 'md' && !!image;

  // It is defined, the eslint rule is failing here.
  // eslint-disable-next-line no-use-before-define
  var ColoredButtonComponent = editorMode ? ColoredButtonEditor : ColoredButton;
  function onClick() {
    if (!editorMode) {
      event({
        // https://dataportal.zillowgroup.net/event-registry/2048
        envelope: {
          event_type_id: '2048',
          event_type_version_id: '1',
          event_template_id: '126',
          event_template_version_id: '1'
          // Master ID: 2284.2.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: 'interaction',
          trigger_location_nm: 'cms',
          trigger_source_nm: 'cms_upsell',
          trigger_object_nm: 'no_trigger_object'
        },
        semantic: {
          semantic_event_nm: 'cms_click'
        },
        cms_info: Object.assign({
          block_id: id || '',
          block_type_cd: 'upsell',
          block_displayed_txt: heading + "\n" + button,
          block_target_url: url,
          block_target_window_txt: ''
        }, getCmsInfoForPage(pageProps))
      });
    }
  }
  return /*#__PURE__*/React.createElement(UpsellCard, {
    button: button,
    className: className,
    color: color,
    editorMode: editorMode,
    heading: heading,
    id: id,
    size: size,
    url: url
  }, /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({
      display: 'flex',
      // To contain the arrow button container.
      position: 'relative'
    }, size === 'xs' && (_ref2 = {
      flexDirection: 'column'
    }, _ref2[MEDIA_DESKTOP] = {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    }, _ref2))
  }, /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({}, hasNonXsArrowCta && (_ref3 = {
      paddingRight: '$spacing.sm'
    }, _ref3[MEDIA_TABLET] = {
      paddingRight: '$spacing.lg',
      '.Upsell__Body, .Upsell__Legal': {
        maxWidth: '250px'
      }
    }, _ref3), hasImage && (_ref4 = {
      paddingRight: '0'
    }, _ref4[MEDIA_DESKTOP] = {
      paddingRight: '$spacing.xxl'
    }, _ref4))
  }, /*#__PURE__*/React.createElement(UpsellHeading, {
    size: size
  }, heading), ['sm', 'md', 'lg'].includes(size) && /*#__PURE__*/React.createElement(Box, {
    className: "Upsell__Body",
    as: "p",
    sx: (_ref5 = {
      // Did not use <Text> for parent as I can't get
      // anything with low line spacing but bigger
      // font size.
      fontSize: '$fontSizes.bodySmall',
      lineHeight: '$lineHeights.finePrint',
      // Box gets default vertical margin, remove that.
      margin: hasNonXsArrowCta && !legal ? '0' : '0 0 $spacing.sm 0'
    }, _ref5[MEDIA_TABLET] = {
      fontSize: '$fontSizes.body',
      lineHeight: '$lineHeights.body'
    }, _ref5)
  }, body), ['sm', 'md', 'lg'].includes(size) && !!legal && /*#__PURE__*/React.createElement(Text, {
    className: "Upsell__Legal",
    as: "p",
    fontType: "bodySmall",
    marginBottom: hasNonXsArrowCta ? undefined : 'sm'
  }, legal), ['sm', 'md', 'lg'].includes(size) && button !== '' && /*#__PURE__*/React.createElement(ColoredButtonComponent, {
    className: "Upsell__Button",
    buttonType: BUTTON_TYPES.primary,
    editorMode: editorMode,
    forwardedAs: "a",
    href: url,
    onClick: yieldCallback(onClick)
    // As body is optional, and legal is optional:
    // * "xs" - Not displayed.
    // * "sm" - When "MEDIA_MOBILE" set marginBottom of
    //   heading, body, and legal to 16. And then
    //   marginTop of 48 here adds up to 64 which is
    //   about the 69 total wanted.
    ,
    marginTop: {
      sm: 'xl',
      md: (_md = {
        default: 'xl'
      }, _md[BREAKPOINT_TABLET] = 'lg', _md),
      lg: (_lg = {
        default: 'xl'
      }, _lg[BREAKPOINT_TABLET] = 'lg', _lg)
    }[size]
  }, button)), (size === 'xs' || hasNonXsArrowCta) && /*#__PURE__*/React.createElement(ColoredIconButton, {
    icon: /*#__PURE__*/React.createElement(IconArrowRight, null),
    title: button,
    href: url,
    forwardedAs: "a",
    onClick: yieldCallback(onClick),
    appearance: "circle",
    marginLeft: "auto",
    className: "Upsell__Button",
    marginTop: size === 'xs' ? (_ref6 = {
      default: 'xl'
    }, _ref6[BREAKPOINT_TABLET] = 'xxl', _ref6[BREAKPOINT_DESKTOP] = 0, _ref6) : undefined
  }), hasImage && /*#__PURE__*/React.createElement(Box, {
    sx: (_ref7 = {
      display: 'none'
    }, _ref7[MEDIA_DESKTOP] = {
      display: 'block'
    }, _ref7.width = '50%', _ref7.flex = 'none', _ref7.img = {
      display: 'block',
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      borderRadius: '$radii.xl'
    }, _ref7)
  }, image)));
};

/** * Subcomponents of the Upsell component ** */
var ColoredButton = /*#__PURE__*/styled(Button).withConfig({
  componentId: "sc-85xoz5-0"
})(["background-color:var(--foreground);border-color:var(--foreground);color:", ";&:visited{background-color:var(--foreground);border-color:var(--foreground);color:", ";}"], token('colors.white'), token('colors.white'));

// Don't use tokens as they dont work in editor
var ColoredButtonEditor = /*#__PURE__*/styled(ColoredButton).withConfig({
  componentId: "sc-85xoz5-1"
})(["padding:6px 8px;border-radius:4px;display:inline-block;"]);
var ColoredIconButton = /*#__PURE__*/styled(IconButton).withConfig({
  componentId: "sc-85xoz5-2"
})(["background-color:var(--foreground);border-color:var(--foreground);color:var(--background);font-weight:normal;align-self:flex-end;&:visited{background-color:var(--foreground);border-color:var(--foreground);color:var(--background);}"]);
Upsell.propTypes = process.env.NODE_ENV !== "production" ? {
  button: PropTypes.string,
  className: PropTypes.string,
  color: ColorPropType.isRequired,
  heading: PropTypes.string.isRequired,
  id: PropTypes.string,
  // This will be `withPreload(Image)` passed in from cms-blocks.
  // Only required for "md" size.
  image: PropTypes.node,
  // Only shows on "lg", "md", and "sm".
  legal: PropTypes.string,
  size: SizePropType.isRequired,
  // Only shows on "lg", "md", and "sm".
  body: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  editorMode: PropTypes.bool
} : {};
Upsell.defaultProps = {
  button: 'Learn More',
  className: undefined,
  editorMode: false,
  id: undefined,
  image: null,
  legal: undefined
};
var Upsell$1 = Upsell;

export { Upsell$1 as default };
